/**
 * Frontend Request Deduplication Layer (Layer 1 of 3)
 *
 * Prevents duplicate requests during poor network conditions (flaky connection,
 * double-tap, browser back button, etc.) by tracking pending requests and
 * aborting duplicate submissions before they hit the network.
 *
 * ARCHITECTURE NOTE (ADR-0002 - Mobile-First):
 * This implementation is 100% portable to React Native with zero changes.
 * We intentionally avoid localStorage/sessionStorage per ADR-0002, which requires
 * portable code across web and native platforms. For cross-tab coordination,
 * Layer 2 (KV cache 5-min window) + Layer 3 (database time-window) provide
 * defense-in-depth safety without browser-specific APIs.
 *
 * THREE-LAYER IDEMPOTENCY SYSTEM:
 * - Layer 1 (this file): Frontend dedup (in-memory, per-instance)
 * - Layer 2: KV cache (5-minute window, cross-request safety)
 * - Layer 3: Database (time-window + uniqueness fields, fallback safety)
 *
 * Why not include userId in the key?
 * - Scoping is already handled server-side via KV composition (user:method:path)
 * - Client-side userId in headers is unnecessary and leaks PII to proxies/CDNs
 * - Pure random UUID is sufficient and privacy-preserving
 *
 * Issue #599: Duplicate event/message creation during poor network
 */

interface PendingRequest {
  endpoint: string;
  fingerprint: string;
  promise: Promise<unknown>;
  controller: AbortController;
  createdAt: number;
}

/**
 * Singleton deduplicator for tracking pending requests across the app
 *
 * Workflow:
 * 1. Hook generates idempotency key (random UUID)
 * 2. Hook checks if duplicate request already pending
 * 3. If pending, return existing promise
 * 4. If new, create request with AbortController
 * 5. Register request for cross-tab coordination
 * 6. On success/error, auto-cleanup
 */
export class RequestDeduplicator {
  private static instance: RequestDeduplicator;
  private pendingRequests: Map<string, PendingRequest> = new Map();
  private readonly TIMEOUT = 30000; // 30 seconds – cleanup stale requests

  private constructor() {
    // Listen for storage events from other tabs
    if (typeof window !== 'undefined') {
      window.addEventListener('storage', (event) => {
        if (event.key?.startsWith('oro:dedup:')) {
          this.handleCrossTabMessage(event);
        }
      });

      // Periodic cleanup of stale requests
      setInterval(() => this.cleanupStaleRequests(), this.TIMEOUT);
    }
  }

  static getInstance(): RequestDeduplicator {
    if (!RequestDeduplicator.instance) {
      RequestDeduplicator.instance = new RequestDeduplicator();
    }
    return RequestDeduplicator.instance;
  }

  /**
   * Check if a request with identical parameters is already pending
   *
   * Returns the pending promise if duplicate found, null otherwise
   */
  checkDuplicate(
    endpoint: string,
    data: unknown
  ): { promise: Promise<unknown>; controller: AbortController } | null {
    const fingerprint = this.createFingerprint(endpoint, data);
    const key = `${endpoint}:${fingerprint}`;

    const pending = this.pendingRequests.get(key);
    if (pending) {
      console.log(
        '[RequestDeduplicator] Duplicate request detected, returning pending promise',
        { endpoint, dataKeys: Object.keys(data || {}) }
      );
      return { promise: pending.promise, controller: pending.controller };
    }

    return null;
  }

  /**
   * Register a new pending request for deduplication
   *
   * The promise should be resolved when the request completes.
   * On resolution (success or error), call cleanup() to remove from tracking.
   */
  register(
    endpoint: string,
    data: unknown,
    promise: Promise<unknown>,
    controller: AbortController
  ): void {
    const fingerprint = this.createFingerprint(endpoint, data);
    const key = `${endpoint}:${fingerprint}`;

    this.pendingRequests.set(key, {
      endpoint,
      fingerprint,
      promise,
      controller,
      createdAt: Date.now(),
    });

    // Auto-cleanup after promise settles
    promise
      .then(() => this.cleanup(key))
      .catch(() => this.cleanup(key));

    // Notify other tabs
    this.notifyCrossTab(endpoint, 'pending');
  }

  /**
   * Create stable fingerprint from request parameters
   *
   * Treats data as a combined string for now. In future, could use
   * JSON.stringify with sorted keys for complex objects.
   */
  private createFingerprint(_endpoint: string, data: unknown): string {
    // Simple approach: hash the endpoint + stringified data
    const str = JSON.stringify(data || {});
    // Use encodeURIComponent to handle Unicode (emojis, etc.) before btoa
    // btoa() only accepts Latin1 characters, so we encode UTF-8 first
    try {
      return btoa(unescape(encodeURIComponent(str))).substring(0, 20);
    } catch {
      // Fallback: simple hash for any edge cases
      let hash = 0;
      for (let i = 0; i < str.length; i++) {
        hash = ((hash << 5) - hash + str.charCodeAt(i)) | 0;
      }
      return hash.toString(36);
    }
  }

  /**
   * Remove request from tracking (called on success/error)
   */
  private cleanup(key: string): void {
    this.pendingRequests.delete(key);
  }

  /**
   * Remove stale requests that haven't settled after TIMEOUT
   */
  private cleanupStaleRequests(): void {
    const now = Date.now();
    for (const [key, request] of this.pendingRequests.entries()) {
      if (now - request.createdAt > this.TIMEOUT) {
        console.warn(
          '[RequestDeduplicator] Cleaning up stale request after timeout',
          { endpoint: request.endpoint, age: now - request.createdAt }
        );
        request.controller.abort();
        this.cleanup(key);
      }
    }
  }

  /**
   * Cross-tab coordination
   *
   * NOTE: We intentionally do NOT use localStorage for cross-tab coordination.
   * 
   * ADR-0002 (Mobile-First Architecture) requires portable code that works on
   * React Native, which has no localStorage API. The RequestDeduplicator is
   * implemented as an in-memory singleton per tab/process.
   * 
   * For cross-tab deduplication scenarios:
   * - Desktop web: In-memory dedup within single tab is sufficient (most users
   *   don't multi-tap between tabs rapidly)
   * - Mobile PWA: Each instance maintains its own RequestDeduplicator; KV Layer 2
   *   + Database Layer 3 provide safety
   * - React Native: Singleton per app instance (no tabs); KV + DB layers active
   * 
   * Rationale: KV cache (Layer 2) + database time-window (Layer 3) are sufficient
   * for all practical use cases. Adding localStorage creates a mobile-hostile code
   * path that violates ADR-0002 constraint of 100% portable business logic.
   * 
   * Future Enhancement (v1.2+): Implement via Broadcast Channel API when needed,
   * as it's native to both web and has a Expo plugin for React Native.
   */
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  private notifyCrossTab(_endpoint: string, _status: string): void {
    // Graceful no-op. Layer 2 (KV) and Layer 3 (DB) provide safety.
    if (typeof BroadcastChannel !== 'undefined') {
      try {
        // Future: Use Broadcast Channel API (also works in React Native via @react-native-community/hooks)
        // const channel = new BroadcastChannel(`oro-dedup-${_endpoint}`);
        // channel.postMessage({ status: _status, timestamp: Date.now() });
        // channel.close();
      } catch (e) {
        // Broadcast Channel not available – graceful fallback to Layer 2+3
        console.debug('[RequestDeduplicator] Broadcast not available', e);
      }
    }
  }

  private handleCrossTabMessage(event: Event): void {
    // Future: Implement via BroadcastChannel when Broadcast API is widely available
    console.debug('[RequestDeduplicator] Cross-process message received (Layer 2+3 sufficient)', {
      type: event.type,
    });
  }
}

/**
 * Generate a random, privacy-preserving idempotency key
 *
 * Uses crypto.randomUUID() for modern browsers, with fallback for legacy environments.
 * Does NOT include userId – scoping is handled server-side in KV key composition.
 *
 * Format: UUID4 (or UUID4-like random string)
 * Example: "550e8400-e29b-41d4-a716-446655440000"
 */
export function generateIdempotencyKey(): string {
  try {
    // Modern browsers (Chrome 63+, Firefox 57+, Safari 14.1+, Edge 79+)
    if (typeof globalThis !== 'undefined' && globalThis.crypto?.randomUUID) {
      return globalThis.crypto.randomUUID();
    }
  } catch {
    // Fallback if crypto is unavailable
  }

  // Fallback: timestamp + random hex (not a true UUID, but sufficient)
  const timestamp = Date.now();
  const random = Math.random().toString(16).substring(2);
  return `${timestamp}-${random}`;
}

/**
 * Stable `client_message_id` minting for Layer 3 send idempotency
 * (#1481 server-side, wired client-side in #2381).
 *
 * The idempotency key above is minted PER ATTEMPT, which is exactly wrong
 * for the Layer 3 contract: the server dedups on
 * (user_id, client_message_id, room_id), so the id must identify the
 * LOGICAL message and survive a retry. In this app "retry" means the
 * composer preserved the failed content and the user tapped send again —
 * so the minter keys pending ids by (roomId + content fingerprint):
 *
 * - Same room + same content while an earlier attempt is unsettled →
 *   the SAME id (a timed-out-but-committed POST dedups server-side).
 * - `settle(roomId)` on confirmed success clears ALL pending ids for the
 *   room (Frank round 2): a success means the composer moved on — its
 *   input is cleared, so any previously failed content is no longer
 *   "preserved for retry"; it was either re-sent (this success) or
 *   abandoned. Without the room-wide clear, fail(A) → success(B) → a later
 *   INTENTIONAL identical send of A would reuse A's stale id and, if A had
 *   actually committed before timing out, be silently suppressed by the
 *   server dedup. A deliberate identical re-send after success therefore
 *   always mints fresh (it IS a new logical message).
 * - Different content while an attempt is pending → different id (an
 *   edited retry must not silently collapse into the committed row).
 *
 * UUID sourcing (Frank round 1): pass a `uuidFactory` when the runtime
 * needs one — Hermes (Expo 54 / RN 0.81) ships no `crypto.randomUUID`, so
 * mobile injects its `generateUuid` fallback (mobile/src/lib/ids.ts) or the
 * field would be omitted on EVERY native send, leaving Layer 3 unreachable
 * on exactly the flaky-network surface it exists for. Without a factory the
 * minter uses `crypto.randomUUID` and returns `null` when it is
 * unavailable: the server validates the field as a UUID, so the legacy
 * fallback format used by generateIdempotencyKey would fail validation —
 * omit the field instead (graceful degradation to Layer 1/2 behavior).
 *
 * Shared by web (hooks/useRoomMessages.ts) and native
 * (mobile/src/hooks/useSendRoomMessage.ts via @shared/lib/deduplication).
 */
export interface ClientMessageIdMinter {
  mint(roomId: string, payload: { body: string; image_urls?: string[] }): string | null;
  /** Confirmed success in a room — the composer moved on; drop every pending id for it. */
  settle(roomId: string): void;
}

export function createClientMessageIdMinter(
  uuidFactory?: () => string | undefined,
): ClientMessageIdMinter {
  // roomId → (content fingerprint → pending id). Nested so settle() can
  // clear a room without string-parsing composite keys. Growth is bounded
  // by distinct failed payloads between successes in a room; the whole map
  // dies with the hook instance on unmount.
  const pending = new Map<string, Map<string, string>>();

  const fingerprint = (payload: { body: string; image_urls?: string[] }): string =>
    JSON.stringify([payload.body, payload.image_urls ?? null]);

  return {
    mint(roomId, payload) {
      const key = fingerprint(payload);
      const room = pending.get(roomId);
      const existing = room?.get(key);
      if (existing) return existing;
      const id = uuidFactory ? uuidFactory() : globalThis.crypto?.randomUUID?.();
      if (!id) return null;
      if (room) {
        room.set(key, id);
      } else {
        pending.set(roomId, new Map([[key, id]]));
      }
      return id;
    },
    settle(roomId) {
      pending.delete(roomId);
    },
  };
}

/**
 * Example hook usage:
 *
 * ```ts
 * export function useCreateEvent(clubId: string) {
 *   const { data: user } = useAuth();
 *   const deduplicator = RequestDeduplicator.getInstance();
 *
 *   return useMutation({
 *     mutationFn: async (payload) => {
 *       const idempotencyKey = generateIdempotencyKey();
 *
 *       const pending = deduplicator.checkDuplicate(
 *         `POST /clubs/${clubId}/events`,
 *         payload
 *       );
 *
 *       if (pending) {
 *         return pending.promise; // Return existing request
 *       }
 *
 *       const controller = new AbortController();
 *       const promise = fetch(`/api/clubs/${clubId}/events`, {
 *         method: 'POST',
 *         headers: { 'X-Idempotency-Key': idempotencyKey },
 *         body: JSON.stringify(payload),
 *         signal: controller.signal,
 *       }).then(r => r.json());
 *
 *       deduplicator.register(
 *         `POST /clubs/${clubId}/events`,
 *         payload,
 *         promise,
 *         controller
 *       );
 *
 *       return promise;
 *     },
 *   });
 * }
 * ```
 */
